import { useState, useEffect, useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import _ from 'lodash';

import { flattenRequiredProductFields } from 'common/components/product-add/make-product-active/utils';

import * as minimumRequirementServices from 'services/minimumRequirements';

const defaultEntityQueryOptions = {
  staleTime: Infinity,
  retry: 1,
};

export const getEntityQueryOptions = (entityType) => {
  const lowercaseEntityType = entityType ? entityType.toLowerCase() : 'product';

  const options = {
    product: {
      queryKey: ['pim', 'get-all-minimum-requirement-product-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllProductFields();
      },
      ...defaultEntityQueryOptions,
    },

    member: {
      queryKey: ['member', 'get-all-minimum-requirement-member-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllMemberFields();
      },
      ...defaultEntityQueryOptions,
    },

    asset: {
      queryKey: ['dam', 'get-all-minimum-requirement-asset-field'],
      queryFn: async () => {
        return await minimumRequirementServices.getAllAssetFields();
      },
      ...defaultEntityQueryOptions,
    },
  };

  return options[lowercaseEntityType];
};

export const useAllMinimumRequirementProductFields = () => {
  const query = useQuery(getEntityQueryOptions('product'));

  return {
    allRequiredProductFields:
      useMemo(() => query?.data?.data?.schemas, [query?.data]) ?? [],

    flatRequiredProductFields: useMemo(
      () =>
        flattenRequiredProductFields({ allFields: query?.data?.data?.schemas }),
      [query?.data]
    ),
    ...query,
  };
};

export const useAllMinimumRequirementMemberFields = () => {
  const query = useQuery(getEntityQueryOptions('member'));

  return {
    allRequiredMemberFields:
      useMemo(() => query?.data?.data?.fields, [query?.data]) ?? [],
    ...query,
  };
};

export const useAllMinimumRequirementAssetFields = () => {
  const query = useQuery(getEntityQueryOptions('asset'));

  return {
    allRequiredAssetFields:
      useMemo(() => query?.data?.data?.fields, [query?.data]) ?? [],
    ...query,
  };
};

export const useGetEntityProperties = (props) => {
  const { entityType: entityTypeProp, fetchOnMount } = props;

  const entityType = entityTypeProp ? entityTypeProp.toLowerCase() : 'product';

  const [fetchStatus, setFetchStatus] = useState('idle');

  const entityProperties = window.entityProperties?.[entityType];

  const fetchEntityProperties = () => {
    if (entityProperties)
      return new Promise((resolve) => resolve(entityProperties));

    const getEntityPropertiesServices = {
      product: minimumRequirementServices.getAllProductFields,
      member: minimumRequirementServices.getAllMemberFields,
      asset: minimumRequirementServices.getAllAssetFields,
    };

    const service = getEntityPropertiesServices[entityType];
    setFetchStatus('pending');

    return (
      service &&
      service()
        .then((response) => {
          const { isSuccess, data } = response;

          if (isSuccess && data) {
            _.set(
              window,
              ['entityProperties', entityType],
              response?.data?.schemas || response?.data?.fields
            );

            return data.fields;
          }
        })
        .finally(() => {
          setFetchStatus('idle');
        })
    );
  };

  const getEntityProperties = () => {
    return window.entityProperties?.[entityType];
  };

  useEffect(() => {
    if (fetchOnMount) fetchEntityProperties();
  }, [fetchOnMount]);

  return {
    status: fetchStatus,
    getEntityProperties,
    fetchEntityProperties,
  };
};
